/**
 * FikoScreenshot - 3D Viewport Screenshot & Video Export Servisi
 * PNG/JPEG formatında yüksek çözünürlüklü render export
 * WebM formatında video kayıt
 *
 * @author FikoYT
 * @version 1.1.0
 */

import * as THREE from 'three';

// ========== VIDEO RECORDING ==========

export interface VideoOptions {
  /** Video formatı */
  format: 'webm' | 'mp4';
  /** FPS (kare/saniye) */
  fps: 30 | 60;
  /** Video kalitesi (bitrate Mbps) */
  quality: 'low' | 'medium' | 'high';
  /** Maksimum süre (saniye, 0 = sınırsız) */
  maxDuration: number;
  /**
   * Kaynak tipi:
   * - canvas: Sadece 3D WebGL canvas
   * - viewport: 3D + HTML dialoglar (composite)
   * - screen: Tüm ekran (getDisplayMedia ile)
   */
  source?: 'canvas' | 'viewport' | 'screen';
  /** Özel imleç göster (viewport modunda) */
  showCursor?: boolean;
}

export interface VideoResult {
  blob: Blob;
  url: string;
  duration: number;
  width: number;
  height: number;
}

export interface ScreenshotOptions {
  /** Export formatı */
  format: 'png' | 'jpeg';
  /** Çözünürlük çarpanı (1x, 2x, 4x) */
  scale: 1 | 2 | 4;
  /** Transparan arka plan (sadece PNG için) */
  transparent: boolean;
  /** Annotation'ları dahil et */
  includeAnnotations: boolean;
  /** JPEG kalitesi (0-1) */
  quality: number;
  /** Arkaplan rengi (hex) */
  backgroundColor?: string;
  /** Dosya adı (opsiyonel) */
  filename?: string;
}

export interface ScreenshotResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

export class FikoScreenshot {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
  }

  /**
   * Renderer, scene ve camera'yı güncelle
   */
  guncelle(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera): void {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
  }

  /**
   * Screenshot al ve Blob olarak döndür
   */
  async capture(options: Partial<ScreenshotOptions> = {}): Promise<ScreenshotResult> {
    const opts: ScreenshotOptions = {
      format: options.format ?? 'png',
      scale: options.scale ?? 1,
      transparent: options.transparent ?? false,
      includeAnnotations: options.includeAnnotations ?? true,
      quality: options.quality ?? 0.92,
      backgroundColor: options.backgroundColor ?? '#1a1a2e',
      filename: options.filename,
    };

    // Mevcut renderer boyutunu al
    const currentSize = new THREE.Vector2();
    this.renderer.getSize(currentSize);

    // Hedef boyut
    const targetWidth = Math.floor(currentSize.x * opts.scale);
    const targetHeight = Math.floor(currentSize.y * opts.scale);

    // Geçici canvas oluştur (yüksek çözünürlük için)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = targetWidth;
    tempCanvas.height = targetHeight;

    // Mevcut renderer ayarlarını sakla
    const originalPixelRatio = this.renderer.getPixelRatio();
    const originalClearColor = this.renderer.getClearColor(new THREE.Color());
    const originalClearAlpha = this.renderer.getClearAlpha();
    const originalSceneBackground = this.scene.background;

    // Arka plan ayarı
    if (opts.transparent && opts.format === 'png') {
      // Transparan için hem renderer hem scene arka planını kaldır
      this.renderer.setClearColor(0x000000, 0);
      this.scene.background = null;
    } else if (opts.backgroundColor) {
      this.renderer.setClearColor(new THREE.Color(opts.backgroundColor), 1);
    }

    // Yüksek çözünürlük için renderer'ı ayarla
    this.renderer.setPixelRatio(opts.scale);
    this.renderer.setSize(targetWidth / opts.scale, targetHeight / opts.scale);

    // Kamerayı güncelle
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = targetWidth / targetHeight;
      this.camera.updateProjectionMatrix();
    } else if (this.camera instanceof THREE.OrthographicCamera) {
      // Ortho kamera için aspect güncelle
      const aspect = targetWidth / targetHeight;
      const frustumHeight = this.camera.top - this.camera.bottom;
      this.camera.left = -frustumHeight * aspect / 2;
      this.camera.right = frustumHeight * aspect / 2;
      this.camera.updateProjectionMatrix();
    }

    // Sahneyi render et
    this.renderer.render(this.scene, this.camera);

    // Canvas'tan image data al
    const gl = this.renderer.getContext();
    const pixels = new Uint8Array(targetWidth * targetHeight * 4);
    gl.readPixels(0, 0, targetWidth, targetHeight, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Geçici canvas'a çiz (WebGL y-flip düzeltmesi)
    const ctx = tempCanvas.getContext('2d')!;
    const imageData = ctx.createImageData(targetWidth, targetHeight);

    // Y eksenini çevir (WebGL koordinat sistemi farklı)
    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        const srcIdx = ((targetHeight - y - 1) * targetWidth + x) * 4;
        const dstIdx = (y * targetWidth + x) * 4;
        imageData.data[dstIdx] = pixels[srcIdx];
        imageData.data[dstIdx + 1] = pixels[srcIdx + 1];
        imageData.data[dstIdx + 2] = pixels[srcIdx + 2];
        imageData.data[dstIdx + 3] = pixels[srcIdx + 3];
      }
    }
    ctx.putImageData(imageData, 0, 0);

    // Orijinal ayarları geri yükle
    this.renderer.setPixelRatio(originalPixelRatio);
    this.renderer.setSize(currentSize.x, currentSize.y);
    this.renderer.setClearColor(originalClearColor, originalClearAlpha);
    this.scene.background = originalSceneBackground;

    // Kamerayı orijinal aspect'e döndür
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = currentSize.x / currentSize.y;
      this.camera.updateProjectionMatrix();
    }

    // Blob oluştur
    const mimeType = opts.format === 'png' ? 'image/png' : 'image/jpeg';
    const dataUrl = tempCanvas.toDataURL(mimeType, opts.quality);

    const blob = await new Promise<Blob>((resolve) => {
      tempCanvas.toBlob((b) => resolve(b!), mimeType, opts.quality);
    });

    return {
      blob,
      dataUrl,
      width: targetWidth,
      height: targetHeight,
    };
  }

  /**
   * Hızlı screenshot - mevcut viewport'u direkt export et
   */
  async captureQuick(): Promise<ScreenshotResult> {
    // preserveDrawingBuffer kontrolü
    const domElement = this.renderer.domElement;
    const dataUrl = domElement.toDataURL('image/png');

    const blob = await fetch(dataUrl).then(r => r.blob());

    return {
      blob,
      dataUrl,
      width: domElement.width,
      height: domElement.height,
    };
  }

  /**
   * Screenshot'ı indir
   */
  async download(options: Partial<ScreenshotOptions> = {}): Promise<void> {
    const result = await this.capture(options);

    const filename = options.filename ?? this.generateFilename(options.format ?? 'png');

    // Download link oluştur
    const link = document.createElement('a');
    link.href = result.dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Screenshot'ı clipboard'a kopyala
   */
  async copyToClipboard(options: Partial<ScreenshotOptions> = {}): Promise<boolean> {
    try {
      const result = await this.capture({ ...options, format: 'png' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': result.blob
        })
      ]);

      return true;
    } catch (error) {
      console.error('Clipboard kopyalama hatası:', error);
      return false;
    }
  }

  /**
   * Dosya adı oluştur
   */
  private generateFilename(format: 'png' | 'jpeg' | 'webm'): string {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const ext = format === 'jpeg' ? 'jpg' : format;
    return `FikoYT-3D-${timestamp}.${ext}`;
  }

  // ========== VIDEO RECORDING ==========

  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingStartTime: number = 0;
  private isRecording: boolean = false;
  private maxDurationTimeout: number | null = null;
  private screenStream: MediaStream | null = null;

  // Viewport (composite) kayıt için
  private compositeCanvas: HTMLCanvasElement | null = null;
  private compositeCtx: CanvasRenderingContext2D | null = null;
  private viewportAnimationId: number | null = null;
  private viewportContainer: HTMLElement | null = null;
  private viewportShowCursor: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;

  /**
   * Video kaydını başlat (Canvas modu - senkron)
   */
  startRecording(options: Partial<VideoOptions> = {}): boolean {
    if (this.isRecording) {
      console.warn('Kayıt zaten devam ediyor');
      return false;
    }

    const opts: VideoOptions = {
      format: options.format ?? 'webm',
      fps: options.fps ?? 30,
      quality: options.quality ?? 'medium',
      maxDuration: options.maxDuration ?? 0,
      source: options.source ?? 'canvas',
      showCursor: options.showCursor ?? false,
    };

    // Ekran modu için async metod kullan
    if (opts.source === 'screen') {
      console.warn('Ekran modu için startScreenRecording() kullanın');
      return false;
    }

    // Bitrate hesapla
    const bitrateMap = { low: 2500000, medium: 5000000, high: 10000000 };
    const bitrate = bitrateMap[opts.quality];

    // Canvas stream al
    const canvas = this.renderer.domElement;
    const stream = canvas.captureStream(opts.fps);

    return this.initRecorder(stream, bitrate, opts.maxDuration);
  }

  /**
   * Ekran kaydını başlat (imleç + dialoglar dahil)
   * @returns Promise<boolean> - Başarılı mı
   */
  async startScreenRecording(options: Partial<VideoOptions> = {}): Promise<boolean> {
    if (this.isRecording) {
      console.warn('Kayıt zaten devam ediyor');
      return false;
    }

    const opts: VideoOptions = {
      format: options.format ?? 'webm',
      fps: options.fps ?? 30,
      quality: options.quality ?? 'medium',
      maxDuration: options.maxDuration ?? 0,
      source: 'screen',
      showCursor: true,
    };

    // Bitrate hesapla
    const bitrateMap = { low: 2500000, medium: 5000000, high: 10000000 };
    const bitrate = bitrateMap[opts.quality];

    try {
      // Ekran yakalama izni iste
      // cursor seçeneği DisplayMediaStreamOptions'da var ama TypeScript tip tanımlarında eksik
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: opts.fps,
        } as MediaTrackConstraints,
        audio: false,
      } as DisplayMediaStreamOptions);

      // Kullanıcı paylaşımı durdurursa
      this.screenStream.getVideoTracks()[0].onended = () => {
        if (this.isRecording) {
          this.stopRecording();
        }
      };

      return this.initRecorder(this.screenStream, bitrate, opts.maxDuration);
    } catch (e) {
      console.error('Ekran yakalama hatası:', e);
      return false;
    }
  }

  /**
   * Viewport kaydını başlat (3D + HTML dialoglar composite)
   * Canvas üzerine HTML overlay'ları da çizer
   * @returns boolean - Başarılı mı
   */
  startViewportRecording(options: Partial<VideoOptions> = {}): boolean {
    if (this.isRecording) {
      console.warn('Kayıt zaten devam ediyor');
      return false;
    }

    const opts: VideoOptions = {
      format: options.format ?? 'webm',
      fps: options.fps ?? 30,
      quality: options.quality ?? 'medium',
      maxDuration: options.maxDuration ?? 0,
      source: 'viewport',
      showCursor: options.showCursor ?? true,
    };

    // Bitrate hesapla
    const bitrateMap = { low: 2500000, medium: 5000000, high: 10000000 };
    const bitrate = bitrateMap[opts.quality];

    // 3D canvas'ın parent container'ını bul
    const canvas3D = this.renderer.domElement;
    this.viewportContainer = canvas3D.parentElement;

    if (!this.viewportContainer) {
      console.error('Viewport container bulunamadı');
      return false;
    }

    // Composite canvas oluştur
    const containerRect = this.viewportContainer.getBoundingClientRect();
    this.compositeCanvas = document.createElement('canvas');
    this.compositeCanvas.width = containerRect.width * window.devicePixelRatio;
    this.compositeCanvas.height = containerRect.height * window.devicePixelRatio;
    this.compositeCtx = this.compositeCanvas.getContext('2d')!;

    // İmleç takibi (document seviyesinde)
    this.viewportShowCursor = opts.showCursor ?? true;
    this.lastMouseX = -100; // Başlangıçta görünmez
    this.lastMouseY = -100;

    // Mouse pozisyonunu sürekli takip et
    this.mouseMoveHandler = (e: MouseEvent) => {
      if (!this.viewportContainer) return;
      const rect = this.viewportContainer.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      // Viewport-relative koordinatlar
      this.lastMouseX = (e.clientX - rect.left) * dpr;
      this.lastMouseY = (e.clientY - rect.top) * dpr;
    };
    document.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });

    // Composite canvas'tan stream al
    const stream = this.compositeCanvas.captureStream(opts.fps);

    // Frame render döngüsü başlat
    const frameInterval = 1000 / opts.fps;
    let lastFrameTime = 0;

    const renderFrame = (timestamp: number) => {
      if (!this.isRecording) return;

      if (timestamp - lastFrameTime >= frameInterval) {
        this.renderViewportFrame();
        lastFrameTime = timestamp;
      }

      this.viewportAnimationId = requestAnimationFrame(renderFrame);
    };

    // Recorder başlat
    const success = this.initRecorder(stream, bitrate, opts.maxDuration);

    if (success) {
      this.viewportAnimationId = requestAnimationFrame(renderFrame);
    }

    return success;
  }

  /**
   * Viewport frame'ini composite canvas'a render et
   */
  private renderViewportFrame(): void {
    if (!this.compositeCanvas || !this.compositeCtx || !this.viewportContainer) return;

    const ctx = this.compositeCtx;
    const canvas3D = this.renderer.domElement;
    const containerRect = this.viewportContainer.getBoundingClientRect();
    const dpr = window.devicePixelRatio;

    // Canvas'ı temizle
    ctx.clearRect(0, 0, this.compositeCanvas.width, this.compositeCanvas.height);

    // 1. 3D Canvas'ı çiz - Önce sahneyi tekrar renderla
    this.renderer.render(this.scene, this.camera);

    // WebGL'den pixel oku ve çiz (preserveDrawingBuffer olmadan da çalışır)
    const gl = this.renderer.getContext();
    const w = canvas3D.width;
    const h = canvas3D.height;

    // Pixel verisi oku
    const pixels = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    // Geçici canvas'a çevir (Y-flip düzeltmesi)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    const tempCtx = tempCanvas.getContext('2d')!;
    const imageData = tempCtx.createImageData(w, h);

    // Y eksenini çevir (WebGL koordinat sistemi farklı)
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const srcIdx = ((h - y - 1) * w + x) * 4;
        const dstIdx = (y * w + x) * 4;
        imageData.data[dstIdx] = pixels[srcIdx];
        imageData.data[dstIdx + 1] = pixels[srcIdx + 1];
        imageData.data[dstIdx + 2] = pixels[srcIdx + 2];
        imageData.data[dstIdx + 3] = pixels[srcIdx + 3];
      }
    }
    tempCtx.putImageData(imageData, 0, 0);

    // Composite canvas'a çiz
    ctx.drawImage(tempCanvas, 0, 0, w, h, 0, 0, this.compositeCanvas.width, this.compositeCanvas.height);

    // 2. Semantic UI Modal Dialog'ları çiz (.ui.modal.visible veya .ui.modal.active)
    const modals = document.querySelectorAll('.ui.modal.visible, .ui.modal.active, .ui.modal:not(.hidden)');
    modals.forEach((modal) => {
      const el = modal as HTMLElement;
      // Görünür mü kontrol et
      const style = window.getComputedStyle(el);
      if (style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0) {
        this.drawSemanticModalToCanvas(el, containerRect, dpr);
      }
    });

    // 3. Toolbar'ları çiz (solAracCubugu, altAracCubugu)
    const toolbars = document.querySelectorAll('.solAracCubugu, .altAracCubugu');
    toolbars.forEach((toolbar) => {
      this.drawElementToCanvas(toolbar as HTMLElement, containerRect, dpr);
    });

    // 4. İmleç çiz (viewport içindeyse)
    if (this.viewportShowCursor) {
      const cw = this.compositeCanvas.width;
      const ch = this.compositeCanvas.height;
      // Viewport içinde mi kontrol et
      if (this.lastMouseX >= 0 && this.lastMouseX <= cw &&
          this.lastMouseY >= 0 && this.lastMouseY <= ch) {
        this.drawCursor(ctx, this.lastMouseX, this.lastMouseY);
      }
    }
  }

  /**
   * Semantic UI Modal'ı canvas'a çiz (saydamlık destekli)
   */
  private drawSemanticModalToCanvas(modal: HTMLElement, containerRect: DOMRect, dpr: number): void {
    if (!this.compositeCtx) return;

    const ctx = this.compositeCtx;
    const modalRect = modal.getBoundingClientRect();

    // Modal ekranda görünür mü?
    if (modalRect.width === 0 || modalRect.height === 0) return;

    // Viewport'a göre pozisyon hesapla
    const x = (modalRect.left - containerRect.left) * dpr;
    const y = (modalRect.top - containerRect.top) * dpr;
    const w = modalRect.width * dpr;
    const h = modalRect.height * dpr;

    // Ekran dışındaysa çizme
    if (x + w < 0 || x > this.compositeCanvas!.width ||
        y + h < 0 || y > this.compositeCanvas!.height) return;

    ctx.save();

    // CSS opacity değerini al ve uygula
    const computedStyle = window.getComputedStyle(modal);
    const cssOpacity = parseFloat(computedStyle.opacity) || 1;
    ctx.globalAlpha = cssOpacity;

    // Gölge
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 20 * dpr;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 5 * dpr;

    // Modal arka planı - saydamlık ile
    let bgColor = computedStyle.backgroundColor;
    if (!bgColor || bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
      bgColor = 'rgba(0, 40, 60, 0.85)'; // Varsayılan yarı saydam
    }
    // Eğer solid renk ise yarı saydam yap
    if (bgColor.startsWith('rgb(')) {
      bgColor = bgColor.replace('rgb(', 'rgba(').replace(')', ', 0.9)');
    }

    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 8 * dpr);
    ctx.fill();

    // Gölgeyi kapat
    ctx.shadowColor = 'transparent';

    // Cam efekti için üst katman (hafif gradient)
    const gradient = ctx.createLinearGradient(x, y, x, y + h);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.02)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.1)');
    ctx.fillStyle = gradient;
    ctx.fill();

    // Kenarlık (parlak)
    ctx.strokeStyle = 'rgba(0, 200, 220, 0.6)';
    ctx.lineWidth = 1.5 * dpr;
    ctx.stroke();

    // Başlık çubuğu (.baslikMove veya .header)
    const header = modal.querySelector('.baslikMove, .header, [class*="baslik"]') as HTMLElement;
    if (header) {
      const headerRect = header.getBoundingClientRect();
      const hx = (headerRect.left - containerRect.left) * dpr;
      const hy = (headerRect.top - containerRect.top) * dpr;
      const hw = headerRect.width * dpr;
      const hh = headerRect.height * dpr;

      // Başlık arka planı (biraz daha koyu)
      ctx.fillStyle = 'rgba(0, 50, 70, 0.95)';
      ctx.beginPath();
      ctx.roundRect(hx, hy, hw, hh, [8 * dpr, 8 * dpr, 0, 0]);
      ctx.fill();

      // Alt kenarlık
      ctx.strokeStyle = 'rgba(0, 180, 200, 0.4)';
      ctx.lineWidth = 1 * dpr;
      ctx.beginPath();
      ctx.moveTo(hx, hy + hh);
      ctx.lineTo(hx + hw, hy + hh);
      ctx.stroke();

      // Başlık metni
      const titleText = header.textContent?.trim() || '';
      if (titleText) {
        ctx.fillStyle = 'rgba(0, 230, 255, 1)';
        ctx.font = `bold ${13 * dpr}px Arial, sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.fillText(titleText.substring(0, 30), hx + 12 * dpr, hy + hh / 2);
      }
    }

    // İçerik alanı - child elementleri recursive çiz
    this.drawModalContent(modal, containerRect, dpr);

    ctx.restore();
  }

  /**
   * Modal içeriğini çiz (input, button, label vb.)
   */
  private drawModalContent(modal: HTMLElement, containerRect: DOMRect, dpr: number): void {
    if (!this.compositeCtx) return;
    const ctx = this.compositeCtx;

    // Butonları çiz
    const buttons = modal.querySelectorAll('button, .button, .ui.button');
    buttons.forEach((btn) => {
      const el = btn as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const bx = (rect.left - containerRect.left) * dpr;
      const by = (rect.top - containerRect.top) * dpr;
      const bw = rect.width * dpr;
      const bh = rect.height * dpr;

      const style = window.getComputedStyle(el);
      ctx.fillStyle = style.backgroundColor || '#2185d0';
      ctx.beginPath();
      ctx.roundRect(bx, by, bw, bh, 4 * dpr);
      ctx.fill();

      // Buton metni
      const text = el.textContent?.trim() || '';
      if (text && text.length < 20) {
        ctx.fillStyle = style.color || '#fff';
        ctx.font = `${11 * dpr}px Arial`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(text, bx + bw / 2, by + bh / 2);
        ctx.textAlign = 'left';
      }
    });

    // Input/Select alanları
    const inputs = modal.querySelectorAll('input, select, textarea');
    inputs.forEach((inp) => {
      const el = inp as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const ix = (rect.left - containerRect.left) * dpr;
      const iy = (rect.top - containerRect.top) * dpr;
      const iw = rect.width * dpr;
      const ih = rect.height * dpr;

      const style = window.getComputedStyle(el);
      ctx.fillStyle = style.backgroundColor || 'rgba(0,60,80,0.8)';
      ctx.strokeStyle = style.borderColor || 'rgba(0,200,220,0.4)';
      ctx.lineWidth = 1 * dpr;

      ctx.beginPath();
      ctx.roundRect(ix, iy, iw, ih, 3 * dpr);
      ctx.fill();
      ctx.stroke();

      // Input değeri veya placeholder
      const value = (el as HTMLInputElement).value || (el as HTMLInputElement).placeholder || '';
      if (value) {
        ctx.fillStyle = style.color || '#fff';
        ctx.font = `${10 * dpr}px Arial`;
        ctx.textBaseline = 'middle';
        ctx.fillText(value.substring(0, 25), ix + 6 * dpr, iy + ih / 2);
      }
    });

    // Label'lar
    const labels = modal.querySelectorAll('label, .label');
    labels.forEach((lbl) => {
      const el = lbl as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const lx = (rect.left - containerRect.left) * dpr;
      const ly = (rect.top - containerRect.top) * dpr;

      const style = window.getComputedStyle(el);
      const text = el.textContent?.trim() || '';
      if (text) {
        ctx.fillStyle = style.color || 'rgba(0,220,240,0.9)';
        ctx.font = `${10 * dpr}px Arial`;
        ctx.textBaseline = 'top';
        ctx.fillText(text.substring(0, 30), lx, ly);
      }
    });

    // Progress bar / slider
    const sliders = modal.querySelectorAll('input[type="range"], .progress, .slider');
    sliders.forEach((slider) => {
      const el = slider as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const sx = (rect.left - containerRect.left) * dpr;
      const sy = (rect.top - containerRect.top) * dpr;
      const sw = rect.width * dpr;
      const sh = rect.height * dpr;

      // Track
      ctx.fillStyle = 'rgba(0,60,80,0.6)';
      ctx.beginPath();
      ctx.roundRect(sx, sy + sh / 2 - 2 * dpr, sw, 4 * dpr, 2 * dpr);
      ctx.fill();

      // Value
      const value = parseFloat((el as HTMLInputElement).value) || 50;
      const max = parseFloat((el as HTMLInputElement).max) || 100;
      const fillWidth = (value / max) * sw;
      ctx.fillStyle = 'rgba(0,200,220,0.8)';
      ctx.beginPath();
      ctx.roundRect(sx, sy + sh / 2 - 2 * dpr, fillWidth, 4 * dpr, 2 * dpr);
      ctx.fill();
    });

    // Checkbox
    const checkboxes = modal.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((cb) => {
      const el = cb as HTMLInputElement;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const cx = (rect.left - containerRect.left) * dpr;
      const cy = (rect.top - containerRect.top) * dpr;
      const size = 14 * dpr;

      ctx.strokeStyle = 'rgba(0,200,220,0.6)';
      ctx.lineWidth = 1 * dpr;
      ctx.strokeRect(cx, cy, size, size);

      if (el.checked) {
        ctx.fillStyle = 'rgba(0,200,220,0.8)';
        ctx.fillRect(cx + 2 * dpr, cy + 2 * dpr, size - 4 * dpr, size - 4 * dpr);
      }
    });
  }

  /**
   * Dialog'u canvas'a çiz
   */
  private drawDialogToCanvas(dialog: HTMLElement, containerRect: DOMRect, dpr: number): void {
    if (!this.compositeCtx || !dialog) return;

    const ctx = this.compositeCtx;
    const dialogRect = dialog.getBoundingClientRect();

    // Dialog viewport içinde mi kontrol et
    const isInsideViewport =
      dialogRect.right > containerRect.left &&
      dialogRect.left < containerRect.right &&
      dialogRect.bottom > containerRect.top &&
      dialogRect.top < containerRect.bottom;

    if (!isInsideViewport) return;

    // Viewport'a göre pozisyon hesapla
    const x = (dialogRect.left - containerRect.left) * dpr;
    const y = (dialogRect.top - containerRect.top) * dpr;
    const w = dialogRect.width * dpr;
    const h = dialogRect.height * dpr;

    // Dialog arka planı
    const computedStyle = window.getComputedStyle(dialog);
    const bgColor = computedStyle.backgroundColor || 'rgba(0, 40, 60, 0.95)';

    ctx.save();

    // Gölge
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 10 * dpr;
    ctx.shadowOffsetX = 2 * dpr;
    ctx.shadowOffsetY = 2 * dpr;

    // Arka plan
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 6 * dpr);
    ctx.fill();

    // Kenarlık
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = 'rgba(0, 200, 220, 0.4)';
    ctx.lineWidth = 1 * dpr;
    ctx.stroke();

    // Başlık çubuğu
    const headerEl = dialog.querySelector('.dialog-header, .fiko-dialog-header, [class*="header"]');
    if (headerEl) {
      const headerRect = headerEl.getBoundingClientRect();
      const headerH = headerRect.height * dpr;

      ctx.fillStyle = 'rgba(0, 60, 80, 0.8)';
      ctx.beginPath();
      ctx.roundRect(x, y, w, headerH, [6 * dpr, 6 * dpr, 0, 0]);
      ctx.fill();

      // Başlık metni
      const titleEl = headerEl.querySelector('.dialog-title, .fiko-dialog-title, span, div');
      if (titleEl) {
        ctx.fillStyle = 'rgba(0, 220, 240, 0.9)';
        ctx.font = `bold ${12 * dpr}px Arial`;
        ctx.textBaseline = 'middle';
        ctx.fillText(titleEl.textContent || 'Dialog', x + 10 * dpr, y + headerH / 2);
      }
    }

    ctx.restore();
  }

  /**
   * Genel HTML element'i canvas'a çiz (toolbar vb.)
   */
  private drawElementToCanvas(element: HTMLElement, containerRect: DOMRect, dpr: number): void {
    if (!this.compositeCtx || !element) return;

    const ctx = this.compositeCtx;
    const rect = element.getBoundingClientRect();

    // Viewport'a göre pozisyon
    const x = (rect.left - containerRect.left) * dpr;
    const y = (rect.top - containerRect.top) * dpr;
    const w = rect.width * dpr;
    const h = rect.height * dpr;

    // Arka plan
    const computedStyle = window.getComputedStyle(element);
    const bgColor = computedStyle.backgroundColor || 'rgba(0, 0, 0, 0.7)';

    ctx.save();
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 4 * dpr);
    ctx.fill();

    // Butonları çiz
    const buttons = element.querySelectorAll('button');
    buttons.forEach((btn) => {
      const btnRect = btn.getBoundingClientRect();
      const bx = (btnRect.left - containerRect.left) * dpr;
      const by = (btnRect.top - containerRect.top) * dpr;
      const bw = btnRect.width * dpr;
      const bh = btnRect.height * dpr;

      const btnStyle = window.getComputedStyle(btn);
      ctx.fillStyle = btnStyle.backgroundColor || '#2185d0';
      ctx.beginPath();
      ctx.arc(bx + bw / 2, by + bh / 2, Math.min(bw, bh) / 2, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
  }

  /**
   * İmleç çiz (DPR ölçekli, daha görünür)
   */
  private drawCursor(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const dpr = window.devicePixelRatio || 1;
    const scale = dpr * 1.2; // Biraz daha büyük

    ctx.save();

    // Gölge efekti
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 4 * scale;
    ctx.shadowOffsetX = 2 * scale;
    ctx.shadowOffsetY = 2 * scale;

    // İmleç ok şekli (Windows tarzı)
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.5 * scale;

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + 22 * scale);
    ctx.lineTo(x + 6 * scale, y + 17 * scale);
    ctx.lineTo(x + 10 * scale, y + 26 * scale);
    ctx.lineTo(x + 14 * scale, y + 24 * scale);
    ctx.lineTo(x + 10 * scale, y + 16 * scale);
    ctx.lineTo(x + 16 * scale, y + 16 * scale);
    ctx.closePath();

    ctx.fill();
    ctx.stroke();

    ctx.restore();
  }

  /**
   * MediaRecorder'ı başlat (ortak metod)
   */
  private initRecorder(stream: MediaStream, bitrate: number, maxDuration: number): boolean {
    // MediaRecorder oluştur
    const mimeType = 'video/webm;codecs=vp9';

    if (!MediaRecorder.isTypeSupported(mimeType)) {
      console.error('Bu format desteklenmiyor:', mimeType);
      return false;
    }

    try {
      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: bitrate,
      });
    } catch (e) {
      console.error('MediaRecorder oluşturulamadı:', e);
      return false;
    }

    this.recordedChunks = [];
    this.recordingStartTime = Date.now();
    this.isRecording = true;

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.recordedChunks.push(e.data);
      }
    };

    this.mediaRecorder.start(100); // Her 100ms'de veri al

    // Maksimum süre kontrolü
    if (maxDuration > 0) {
      this.maxDurationTimeout = window.setTimeout(() => {
        this.stopRecording();
      }, maxDuration * 1000);
    }

    return true;
  }

  /**
   * Video kaydını durdur ve sonucu döndür
   */
  async stopRecording(): Promise<VideoResult | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      return null;
    }

    // Timeout temizle
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }

    return new Promise((resolve) => {
      this.mediaRecorder!.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const duration = (Date.now() - this.recordingStartTime) / 1000;

        const canvas = this.renderer.domElement;

        // Ekran stream'i temizle
        if (this.screenStream) {
          this.screenStream.getTracks().forEach(track => track.stop());
          this.screenStream = null;
        }

        // Viewport kayıt temizliği
        if (this.viewportAnimationId) {
          cancelAnimationFrame(this.viewportAnimationId);
          this.viewportAnimationId = null;
        }
        if (this.mouseMoveHandler) {
          document.removeEventListener('mousemove', this.mouseMoveHandler);
          this.mouseMoveHandler = null;
        }
        this.compositeCanvas = null;
        this.compositeCtx = null;
        this.viewportContainer = null;
        this.lastMouseX = 0;
        this.lastMouseY = 0;

        this.isRecording = false;
        this.mediaRecorder = null;
        this.recordedChunks = [];

        resolve({
          blob,
          url,
          duration,
          width: canvas.width,
          height: canvas.height,
        });
      };

      this.mediaRecorder!.stop();
    });
  }

  /**
   * Kayıt durumu
   */
  dispose(): void {
    if (this.isRecording) {
      this.stopRecording();
    }
    if (this.mouseMoveHandler) {
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      this.mouseMoveHandler = null;
    }
    if (this.viewportAnimationId) {
      cancelAnimationFrame(this.viewportAnimationId);
      this.viewportAnimationId = null;
    }
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop());
      this.screenStream = null;
    }
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
    this.compositeCanvas = null;
    this.compositeCtx = null;
    this.viewportContainer = null;
    this.recordedChunks = [];
    this.mediaRecorder = null;
  }

  getRecordingStatus(): { isRecording: boolean; duration: number } {
    return {
      isRecording: this.isRecording,
      duration: this.isRecording ? (Date.now() - this.recordingStartTime) / 1000 : 0,
    };
  }

  /**
   * Video indir
   */
  async downloadVideo(result: VideoResult, filename?: string): Promise<void> {
    const name = filename ?? this.generateFilename('webm');
    const link = document.createElement('a');
    link.href = result.url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
